import React, { useState } from 'react';
import { Link } from 'gatsby';

import { CustomizableButton } from '~components/common/Button';
import ExternalLinkIcon from '~components/common/ExternalLinkIcon';
import { stateEnums } from '~utils/enums';

import { SelectStandalone } from '~components/forms/inputs/Select';
import { miniToolProps } from '../propTypes';

export default function BallotTracker({ stateInfos, overrides }) {
  const [selectedState, setSelectedState] = useState('');

  const selectedStateCode =
    selectedState &&
    stateEnums.find(
      (e) => e.option.toLowerCase() === selectedState.toLowerCase()
    ).value;

  // reprocess stateInfos into a simpler map to deal with
  const stateData = {};
  stateInfos.forEach((state) => {
    const stateName = state.fieldValue;
    const ballotTracker = state.nodes.find(
      (node) => node.field_type === 'gov_tool_absentee_tracker'
    );
    stateData[stateName] = ballotTracker?.text;
  });

  const selectedStateStatewideURL = selectedState && stateData[selectedState];

  const handleChange = (evt) => {
    setSelectedState(evt.target.value);
  };

  const handleClick = (evt) => {
    if (!selectedStateStatewideURL) {
      evt.preventDefault();
      evt.stopPropagation();
    }
  };

  const stateOverrides = overrides
    .filter((stateGroup) => stateGroup.fieldValue === selectedStateCode)[0]
    ?.nodes.filter(
      (override) => override.field.slug === 'gov_tool_absentee_tracker'
    );

  function stateOverridesOptions() {
    return (
      <>
        <p>
          {selectedState}{' '}
          {selectedStateStatewideURL
            ? 'has several regional trackers. Select your region below if it is listed:'
            : 'does not have a statewide ballot tracker, but some regions have trackers:'}
        </p>
        <ul className='pl-5'>
          {stateOverrides.map(({ value, region }) => (
            <li>
              <a
                href={value}
                target='_blank'
                rel='noopener'
                title={`${region.name} Ballot Tracker`}
              >
                {region.name}
                <ExternalLinkIcon />
              </a>
            </li>
          ))}
        </ul>
        <p>
          For all other regions,{' '}
          {selectedStateStatewideURL ? (
            <>
              use the{' '}
              <Link
                to={selectedStateStatewideURL}
                title='statewide ballot tracker'
              >
                statewide ballot tracker
              </Link>
            </>
          ) : (
            <>
              call your{' '}
              <Link
                to={`/local-election-offices/${selectedStateCode}`}
                title='local election office'
              >
                local election office
              </Link>
            </>
          )}
          .
        </p>
      </>
    );
  }

  function noBallotTrackerOptions() {
    return (
      <p>
        {selectedState} does not have a ballot tracker. You can{' '}
        <Link
          to={`/local-election-offices/${selectedStateCode}/`}
          title='local election office'
        >
          contact your local election office
        </Link>{' '}
        directly to ask about the status of your ballot.
      </p>
    );
  }

  return (
    <div className='mb-14'>
      <p>
        Select your state to track the status of your absentee or mail ballot.
        These tools will tell you if your application has been received, if your
        ballot has been mailed to you, and if your voted ballot has been
        counted. If your state doesn&apos;t have a tracker, you can call your{' '}
        <Link to='/local-election-offices/' title='local election office'>
          local election office
        </Link>{' '}
        directly.
      </p>
      <p>
        If you cast a normal ballot on election day,{' '}
        <strong>you do not need to use this tool.</strong> Regular in-person
        ballots are counted right away. If you cast a provisional ballot in
        person, you can track it with your state&apos;s{' '}
        <Link
          to='/provisional-ballot-tracker/'
          title='Provisional Ballot Tracker'
        >
          provisional ballot tracker
        </Link>
        .
      </p>
      <div className='flex max-w-xl flex-row gap-5'>
        <div className='flex-1'>
          <SelectStandalone
            name='state'
            elementId='state'
            onChange={handleChange}
            value={selectedState}
          >
            <option value='' disabled defaultValue>
              Select a state
            </option>
            {Object.keys(stateData).map((stateName) => (
              <option key={stateName} value={stateName}>
                {stateName}
              </option>
            ))}
          </SelectStandalone>
        </div>
        <div className='w-32'>
          <CustomizableButton
            href={selectedStateStatewideURL || '#'}
            onClick={handleClick}
            disabled={!selectedStateStatewideURL || stateOverrides}
            target='_blank'
            rel='noopener'
            elementType='a'
            supplementalStyling='h-12 min-h-0'
            data-element-id='go-button'
          >
            <span>
              Go <ExternalLinkIcon />
            </span>
          </CustomizableButton>
        </div>
      </div>
      {selectedState && (
        <div className='pt-5'>
          {stateOverrides
            ? stateOverridesOptions()
            : !selectedStateStatewideURL && noBallotTrackerOptions()}
        </div>
      )}
    </div>
  );
}

BallotTracker.propTypes = miniToolProps;
